import React, { useState, useEffect, useCallback } from 'react';
import reviewApi from '../../services/reviewApi';
import './AdminReviews.css';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const AdminReviews = ({ setCurrentPage }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [stats, setStats] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);

    // Fetch reviews
    const fetchReviews = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page: currentPage,
                limit: 10,
                sort: 'newest'
            };

            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            if (searchQuery) {
                params.search = searchQuery;
            }

            console.log('Admin fetching reviews with params:', params);
            const response = await reviewApi.getAdminReviews(params);
            console.log('Admin reviews response:', response);

            if (response.success && response.data) {
                setReviews(response.data.reviews || []);
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.total_pages || 1);
                }
            } else if (response.reviews) {
                setReviews(response.reviews || []);
                if (response.pagination) {
                    setTotalPages(response.pagination.total_pages || 1);
                }
            }
        } catch (err) {
            setError('Failed to load reviews. Please try again.');
            console.error('Error fetching admin reviews:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, statusFilter, searchQuery]);

    // Fetch stats
    const fetchStats = useCallback(async () => {
        try {
            const response = await reviewApi.getReviewStats();
            if (response.success && response.data) {
                setStats(response.data);
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    }, []);

    useEffect(() => {
        fetchReviews();
        fetchStats();
    }, [fetchReviews, fetchStats]);

    // Handle status change
    const handleStatusChange = async (reviewId, newStatus) => {
        setActionLoading(reviewId);
        try {
            const response = await reviewApi.updateReviewStatus(reviewId, newStatus);
            if (response.success) {
                // Refresh reviews
                fetchReviews();
                fetchStats();
            }
        } catch (err) {
            alert('Failed to update review status: ' + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    // Handle delete
    const handleDelete = async (reviewId) => {
        if (!window.confirm('Are you sure you want to delete this review?')) {
            return;
        }

        setActionLoading(reviewId);
        try {
            const response = await reviewApi.deleteReview(reviewId);
            if (response.success) {
                fetchReviews();
                fetchStats();
            }
        } catch (err) {
            alert('Failed to delete review: ' + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    // Render stars
    const renderStars = (rating) => {
        return (
            <span className="admin-stars">
                {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} className={star <= rating ? 'filled' : ''}>★</span>
                ))}
            </span>
        );
    };

    // Get status badge class
    const getStatusClass = (status) => {
        switch (status) {
            case 'approved': return 'status-approved';
            case 'pending': return 'status-pending';
            case 'rejected': return 'status-rejected';
            default: return '';
        }
    };

    return (
        <div className="admin-reviews-page">
            <div className="admin-reviews-header">
                <h1>📝 Review Management</h1>
                <button
                    className="back-btn"
                    onClick={() => setCurrentPage('admin-dashboard')}
                >
                    ← Back to Dashboard
                </button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="review-stats-cards">
                    <div className="stat-card">
                        <span className="stat-number">{stats.total_reviews || 0}</span>
                        <span className="stat-label">Total Reviews</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-number">{stats.average_rating?.toFixed(1) || '0.0'}</span>
                        <span className="stat-label">Average Rating</span>
                    </div>
                    <div className="stat-card approved">
                        <span className="stat-number">{stats.approved_count || 0}</span>
                        <span className="stat-label">Approved</span>
                    </div>
                    <div className="stat-card pending">
                        <span className="stat-number">{stats.pending_count || 0}</span>
                        <span className="stat-label">Pending</span>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="review-filters-bar">
                <div className="filter-group">
                    <input
                        type="text"
                        placeholder="Search reviews..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                </div>
                <div className="filter-group">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Status</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
                <button onClick={fetchReviews} className="refresh-btn">
                    🔄 Refresh
                </button>
            </div>

            {/* Reviews Table */}
            <div className="reviews-table-container">
                {loading ? (
                    <LoadingSpinner type="page" message="Loading reviews..." />
                ) : error ? (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={fetchReviews} className="retry-btn">Try Again</button>
                    </div>
                ) : reviews.length === 0 ? (
                    <div className="empty-state">
                        <span className="empty-icon">📭</span>
                        <h3>No Reviews Found</h3>
                        <p>No reviews match your current filters.</p>
                    </div>
                ) : (
                    <table className="reviews-table">
                        <thead>
                            <tr>
                                <th>Reviewer</th>
                                <th>Rating</th>
                                <th>Title</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reviews.map(review => (
                                <tr key={review.id}>
                                    <td>
                                        <div className="reviewer-cell">
                                            <strong>{review.name}</strong>
                                            <span className="email">{review.email}</span>
                                            {review.is_verified_purchase && (
                                                <span className="verified-tag">✓ Verified</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>{renderStars(review.rating)}</td>
                                    <td>
                                        <div className="review-content-cell">
                                            <strong>{review.title}</strong>
                                            <p>{review.comment?.substring(0, 100)}{review.comment?.length > 100 ? '...' : ''}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusClass(review.status)}`}>
                                            {review.status}
                                        </span>
                                    </td>
                                    <td>
                                        {new Date(review.created_at).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            {review.status !== 'approved' && (
                                                <button
                                                    className="action-btn approve"
                                                    onClick={() => handleStatusChange(review.id, 'approved')}
                                                    disabled={actionLoading === review.id}
                                                    title="Approve"
                                                >
                                                    ✓
                                                </button>
                                            )}
                                            {review.status !== 'rejected' && (
                                                <button
                                                    className="action-btn reject"
                                                    onClick={() => handleStatusChange(review.id, 'rejected')}
                                                    disabled={actionLoading === review.id}
                                                    title="Reject"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                            <button
                                                className="action-btn delete"
                                                onClick={() => handleDelete(review.id)}
                                                disabled={actionLoading === review.id}
                                                title="Delete"
                                            >
                                                🗑
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Pagination */}
                {!loading && reviews.length > 0 && totalPages > 1 && (
                    <div className="pagination">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="page-btn"
                        >
                            ← Previous
                        </button>
                        <span className="page-info">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="page-btn"
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminReviews;
